import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { consultationService } from '../services/consultationApi';
import ProfileIncompleteWarning from '../components/ProfileIncompleteWarning';
import AuroraLoadingOverlay from '../components/AuroraLoadingOverlay';
import NoTokensBanner from '../components/NoTokensBanner';
import { renderMarkdown } from '../utils/renderMarkdown';

const MAX_CHARS = 1000;

const KabbalahPage: React.FC = () => {
  const { user, refresh } = useAuth();
  const [question, setQuestion] = useState('');

  useEffect(() => { refresh().catch(() => {}); }, []);
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profile = (user as any)?.profile;
  const missingFields: string[] = [];
  if (!profile?.birth_date) missingFields.push('birth_date');

  const handleSubmit = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);
    setAnswer('');
    try {
      const q = question.trim() || 'Faça uma análise completa da minha Árvore da Vida e Gematria do nome.';
      const result = await consultationService.askAurora(q, 'kabbalah');
      setAnswer(result.general_interpretation);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Erro ao consultar Aurora Lyz');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-white/20">
        <Link to="/dashboard" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </Link>
        <h1 className="text-xl font-mystical font-bold">Kabbalah</h1>
        <div className="w-20" />
      </div>
      <AuroraLoadingOverlay isLoading={isLoading} message="Aurora está meditando..." />

      <div className="container mx-auto px-4 py-5 sm:py-10 max-w-2xl">
        <div className="text-center mb-8">
          <div className="text-3xl sm:text-5xl mb-3">✡️</div>
          <h2 className="text-lg sm:text-2xl font-mystical font-bold mb-2">Kabbalah — Árvore da Vida</h2>
          <p className="text-white/55 text-sm leading-relaxed">
            Aurora Lyz identifica sua Sephirah dominante na Árvore da Vida e analisa a Gematria do seu nome para revelar sua missão espiritual.
          </p>
          <p className="text-amber-400/80 text-xs mt-2">Consome 1 token por consulta</p>
        </div>

        {missingFields.length > 0 && (
          <ProfileIncompleteWarning missingFields={missingFields} profileSection="dados" />
        )}

        {missingFields.length === 0 && (
          <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
            <p className="text-white/50 text-xs mb-1">Dados para a leitura</p>
            <p className="text-white/85 text-sm">
              <span className="font-semibold">{(user as any)?.name}</span>
              {profile?.birth_date && <> · {new Date(profile.birth_date + 'T00:00:00').toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</>}
            </p>
          </div>
        )}

        <AnimatePresence mode="wait">
          {!answer && (
            <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
              <div className="relative mb-4">
                <textarea
                  value={question}
                  onChange={e => setQuestion(e.target.value.slice(0, MAX_CHARS))}
                  placeholder="Opcional: faça uma pergunta específica. Ex: Qual Sephirah rege minha missão de vida? O que meu nome revela pela Gematria?"
                  rows={4}
                  className="w-full border rounded-2xl px-5 py-4 focus:outline-none resize-none text-sm leading-relaxed"
                  style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', caretColor: 'white' }}
                  disabled={isLoading || missingFields.length > 0}
                />
                <span className="absolute bottom-3 right-4 text-white/30 text-xs">{question.length}/{MAX_CHARS}</span>
              </div>

              <NoTokensBanner error={error} />

              <motion.button
                onClick={handleSubmit}
                disabled={isLoading || missingFields.length > 0}
                whileHover={!isLoading && missingFields.length === 0 ? { scale: 1.03 } : {}}
                whileTap={!isLoading && missingFields.length === 0 ? { scale: 0.97 } : {}}
                className="w-full py-3.5 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #1d4ed8, #4f46e5)' }}
              >
                {isLoading ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Aurora está consultando a Árvore…</>
                ) : (
                  <><Sparkles size={18} />Consultar minha Kabbalah</>
                )}
              </motion.button>
            </motion.div>
          )}

          {answer && (
            <motion.div key="answer" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              <div className="rounded-2xl p-4 sm:p-6 mb-6" style={{ background: 'linear-gradient(135deg, rgba(29,78,216,0.15) 0%, rgba(79,70,229,0.12) 100%)', border: '1px solid rgba(79,70,229,0.3)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">✡️</span>
                  <span className="text-indigo-300 font-mystical font-semibold text-sm">Aurora Lyz — Kabbalah</span>
                </div>
                <div className="text-white/90 text-sm leading-relaxed text-justify">{renderMarkdown(answer)}</div>
              </div>
              <button onClick={() => { setAnswer(''); setQuestion(''); setError(null); }} className="w-full py-3 rounded-2xl border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all text-sm font-medium">
                Nova consulta
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default KabbalahPage;

